/**
 * Common event listener management hooks for Svelte 5
 * Provides reusable patterns for event handling with automatic cleanup
 */

import { onMount, onDestroy } from 'svelte';

/**
 * Generic event listener hook with automatic cleanup
 * @param target - Event target (window, document, element, or media query)
 * @param event - Event name
 * @param handler - Event handler function
 * @param options - Event listener options
 */
export function useEventListener<K extends keyof WindowEventMap>(
  target: Window | Document | HTMLElement | MediaQueryList | null | undefined,
  event: K | string,
  handler: (event: any) => void,
  options?: boolean | AddEventListenerOptions
) {
  onMount(() => {
    if (!target) return;
    
    target.addEventListener(event as string, handler, options);
    
    return () => {
      target.removeEventListener(event as string, handler, options);
    };
  });
}

/**
 * Document click outside hook for closing dropdowns/modals
 * @param element - Element to check clicks outside of
 * @param onClickOutside - Callback when clicked outside
 * @param enabled - Whether the listener is active
 */
export function useClickOutside(
  element: HTMLElement | null | undefined | (() => HTMLElement | null | undefined),
  onClickOutside: () => void,
  enabled: boolean | (() => boolean) = true
) {
  function handleClick(event: MouseEvent | PointerEvent) {
    const isEnabled = typeof enabled === 'function' ? enabled() : enabled;
    if (!isEnabled) return;
    
    const el = typeof element === 'function' ? element() : element;
    if (!el) return;
    
    const target = event.target as Node;
    if (!el.contains(target)) {
      onClickOutside();
    }
  }
  
  onMount(() => {
    const isEnabled = typeof enabled === 'function' ? enabled() : enabled;
    if (!isEnabled) return;
    
    // Use capture phase for better dropdown handling
    document.addEventListener('pointerdown', handleClick, true);
    
    return () => {
      document.removeEventListener('pointerdown', handleClick, true);
    };
  });
}

/**
 * Keyboard event handler with key combinations
 * @param keys - Key combinations to listen for
 * @param handler - Handler function
 * @param options - Options for the event listener
 */
export function useKeyboard(
  keys: string | string[],
  handler: (event: KeyboardEvent) => void,
  options?: {
    target?: Window | Document | HTMLElement;
    preventDefault?: boolean;
    stopPropagation?: boolean;
    capture?: boolean;
  }
) {
  const keyArray = Array.isArray(keys) ? keys : [keys];
  const target = options?.target || window;
  
  function handleKeyDown(event: KeyboardEvent) {
    const matchesKey = keyArray.some(key => {
      if (key.includes('+')) {
        // Handle modifier keys
        const parts = key.split('+').map(p => p.trim().toLowerCase());
        const hasCtrl = parts.includes('ctrl') || parts.includes('cmd');
        const hasShift = parts.includes('shift');
        const hasAlt = parts.includes('alt');
        const mainKey = parts[parts.length - 1];
        
        return (
          (!hasCtrl || (event.ctrlKey || event.metaKey)) &&
          (!hasShift || event.shiftKey) &&
          (!hasAlt || event.altKey) &&
          event.key.toLowerCase() === mainKey
        );
      }
      
      return event.key === key || event.code === key;
    });
    
    if (matchesKey) {
      if (options?.preventDefault) event.preventDefault();
      if (options?.stopPropagation) event.stopPropagation();
      handler(event);
    }
  }
  
  onMount(() => {
    target.addEventListener('keydown', handleKeyDown as EventListener, options?.capture);
    
    return () => {
      target.removeEventListener('keydown', handleKeyDown as EventListener, options?.capture);
    };
  });
}

/**
 * Mouse drag handler for resizing/dragging elements (Original version)
 * @param element - Element that triggers the drag
 * @param handlers - Drag event handlers
 */
export function useDrag(
  element: HTMLElement | null | undefined,
  handlers: {
    onDragStart?: (event: MouseEvent) => void;
    onDrag?: (event: MouseEvent) => void;
    onDragEnd?: (event: MouseEvent) => void;
    cursor?: string;
  }
) {
  let isDragging = false;
  
  function handleMouseDown(event: MouseEvent) {
    if (!element || !element.contains(event.target as Node)) return;
    
    isDragging = true;
    handlers.onDragStart?.(event);
    
    if (handlers.cursor) {
      document.body.style.cursor = handlers.cursor;
    }
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }
  
  function handleMouseMove(event: MouseEvent) {
    if (!isDragging) return;
    handlers.onDrag?.(event);
  }
  
  function handleMouseUp(event: MouseEvent) {
    if (!isDragging) return;
    
    isDragging = false;
    handlers.onDragEnd?.(event);
    
    document.body.style.cursor = '';
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  }
  
  onMount(() => {
    if (!element) return;
    
    element.addEventListener('mousedown', handleMouseDown);
    
    return () => {
      element.removeEventListener('mousedown', handleMouseDown);
      // Clean up any lingering listeners
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  });
}

/**
 * Mouse drag handler for Svelte 5 with reactive element references
 * Note: Must be called within a component's $effect for proper reactivity
 * @param getElement - Function that returns the element (for reactive bindings)
 * @param handlers - Drag event handlers
 * @returns Cleanup function to call when unmounting
 */
export function useDragSvelte5(
  getElement: () => HTMLElement | null | undefined,
  handlers: {
    onDragStart?: (event: MouseEvent) => void;
    onDrag?: (event: MouseEvent) => void;
    onDragEnd?: (event: MouseEvent) => void;
    cursor?: string;
  }
): () => void {
  let isDragging = false;
  
  function handleMouseDown(event: MouseEvent) {
    const element = getElement();
    if (!element || !element.contains(event.target as Node)) return;
    
    isDragging = true;
    handlers.onDragStart?.(event);
    
    if (handlers.cursor) {
      document.body.style.cursor = handlers.cursor;
    }
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }
  
  function handleMouseMove(event: MouseEvent) {
    if (!isDragging) return;
    handlers.onDrag?.(event);
  }
  
  function handleMouseUp(event: MouseEvent) {
    if (!isDragging) return;
    
    isDragging = false;
    handlers.onDragEnd?.(event);
    
    document.body.style.cursor = '';
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  }
  
  // Return a setup function to be called in component's $effect
  const element = getElement();
  if (element) {
    element.addEventListener('mousedown', handleMouseDown);
  }
  
  // Return cleanup function
  return () => {
    const el = getElement();
    if (el) {
      el.removeEventListener('mousedown', handleMouseDown);
    }
    // Clean up any lingering listeners
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = '';
  };
}

/**
 * Storage event listener for cross-tab communication
 * @param key - Storage key to watch (optional, watches all if not provided)
 * @param handler - Handler function
 */
export function useStorageListener(
  key: string | null,
  handler: (newValue: string | null, oldValue: string | null) => void
) {
  function handleStorageChange(event: StorageEvent) {
    if (key && event.key !== key) return;
    handler(event.newValue, event.oldValue);
  }
  
  onMount(() => {
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  });
}

/**
 * Media query listener for responsive behavior
 * @param query - Media query string
 * @param handler - Handler function
 */
export function useMediaQuery(
  query: string,
  handler: (matches: boolean) => void
) {
  onMount(() => {
    const mediaQuery = window.matchMedia(query);
    
    // Call handler with initial state
    handler(mediaQuery.matches);
    
    // Modern API
    function handleChange(event: MediaQueryListEvent) {
      handler(event.matches);
    }
    
    mediaQuery.addEventListener('change', handleChange);
    
    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  });
}

/**
 * Custom event listener for app-specific events
 * @param eventName - Custom event name
 * @param handler - Event handler
 */
export function useCustomEvent<T = any>(
  eventName: string,
  handler: (detail: T) => void
) {
  function handleEvent(event: Event) {
    const customEvent = event as CustomEvent<T>;
    handler(customEvent.detail);
  }
  
  onMount(() => {
    window.addEventListener(eventName, handleEvent);
    
    return () => {
      window.removeEventListener(eventName, handleEvent);
    };
  });
}

/**
 * Scroll sync between two elements
 * @param source - Source element to sync from
 * @param target - Target element to sync to
 */
export function useScrollSync(
  source: HTMLElement | null | undefined,
  target: HTMLElement | null | undefined
) {
  let cleanup: (() => void) | null = null;
  let checkInterval: NodeJS.Timeout | null = null;
  
  function syncScroll() {
    if (!source || !target) return;
    
    requestAnimationFrame(() => {
      if (!source || !target) return;
      target.scrollTop = source.scrollTop;
      target.scrollLeft = source.scrollLeft;
    });
  }
  
  function setupListeners() {
    if (!source || !target) return;
    
    source.addEventListener('scroll', syncScroll);
    source.addEventListener('input', syncScroll);
    
    // Initial sync
    syncScroll();
    
    cleanup = () => {
      source.removeEventListener('scroll', syncScroll);
      source.removeEventListener('input', syncScroll);
    };
  }
  
  onMount(() => {
    // Check periodically for element availability
    checkInterval = setInterval(() => {
      if (source && target && !cleanup) {
        setupListeners();
        if (checkInterval) {
          clearInterval(checkInterval);
          checkInterval = null;
        }
      }
    }, 100);
    
    // Timeout after 5 seconds
    setTimeout(() => {
      if (checkInterval) {
        clearInterval(checkInterval);
        checkInterval = null;
      }
    }, 5000);
    
    // Try immediate setup
    setupListeners();
    
    return () => {
      if (cleanup) {
        cleanup();
      }
      if (checkInterval) {
        clearInterval(checkInterval);
      }
    };
  });
}

/**
 * Resize observer hook
 * @param element - Element to observe
 * @param handler - Handler for size changes
 */
export function useResizeObserver(
  element: HTMLElement | null | undefined,
  handler: (entry: ResizeObserverEntry) => void
) {
  onMount(() => {
    if (!element || typeof ResizeObserver === 'undefined') return;
    
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        handler(entry);
      }
    });
    
    observer.observe(element);
    
    return () => {
      observer.disconnect();
    };
  });
}

/**
 * Intersection observer hook for lazy loading, animations, etc.
 * @param element - Element to observe
 * @param handler - Handler for intersection changes
 * @param options - Intersection observer options
 */
export function useIntersectionObserver(
  element: HTMLElement | null | undefined,
  handler: (isIntersecting: boolean, entry: IntersectionObserverEntry) => void,
  options?: IntersectionObserverInit
) {
  onMount(() => {
    if (!element || typeof IntersectionObserver === 'undefined') return;
    
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        handler(entry.isIntersecting, entry);
      }
    }, options);
    
    observer.observe(element);
    
    return () => {
      observer.disconnect();
    };
  });
}